import Image from 'next/image';
import { LocalizedLink } from '@/components/LocalizedLink';
import { formatArticleDate, getArticleDateFields } from '@/lib/blog-content';
import type { ArticleEntry } from '@/lib/cms';
import { absCmsMediaUrl } from '@/lib/cms-media';
import type { Locale } from '@/lib/i18n';
import { getTranslations, t } from '@/lib/translations';

type BlogArticleCardProps = {
  article: ArticleEntry;
  href: string;
  locale: Locale;
};

export function BlogArticleCard({ article, href, locale }: BlogArticleCardProps) {
  const cover = absCmsMediaUrl(article.coverImage?.url);
  const tr = getTranslations(locale);
  const { publishedIso } = getArticleDateFields(article.publishedAt, article.updatedAt);
  const dateLabel = publishedIso ? formatArticleDate(publishedIso, locale, 'short') : '';

  return (
    <LocalizedLink
      href={href}
      className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 transition hover:border-[#ffb74d]/40 hover:bg-white/[0.07]"
    >
      <div className="relative aspect-[16/9] w-full bg-white/5">
        {cover ? (
          <Image
            src={cover}
            alt={article.coverImage?.alternativeText || article.title}
            fill
            className="object-cover transition group-hover:opacity-95"
            sizes="(max-width: 768px) 100vw, 33vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">Blog</div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        {article.category?.name ? (
          <span className="text-xs font-medium uppercase tracking-wide text-[#ffb74d]">
            {article.category.name}
          </span>
        ) : null}
        <h2 className="text-base font-semibold text-white group-hover:text-[#ffb74d]">{article.title}</h2>
        <p className="line-clamp-2 text-sm text-slate-400">
          {article.excerpt || ''}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-2 text-xs text-slate-500">
          {article.author?.name ? <span>{article.author.name}</span> : null}
          {dateLabel && publishedIso ? (
            <>
              <span aria-hidden>·</span>
              <time dateTime={publishedIso}>{t(tr, 'blog.published', { date: dateLabel })}</time>
            </>
          ) : null}
        </div>
      </div>
    </LocalizedLink>
  );
}
